// libraries
import type { LoaderActionConfig } from '@makemydeal/dr-sales-checkin-store';
import { Actions, loaderActionConfig } from '@makemydeal/dr-sales-checkin-store';
import {
    FETCH_VEHICLE_DETAILS_REQUEST,
    FETCH_VEHICLE_DETAILS_SUCCESS,
    FETCH_VEHICLE_DETAILS_FAILURE
} from '@makemydeal/dr-activities-vehicle-details';
import {
    FETCH_DEAL_SUMMARY_PDF_REQUEST,
    FETCH_DEAL_SUMMARY_PDF_FAILURE,
    ACCEPT_DEAL_SUMMARY_REQUEST,
    SEND_DEAL_SUMMARY_REQUEST_LOADER,
    DEAL_PDF_LOADED_SUCCESS,
    DEAL_PROPOSAL_FETCH_REQUEST,
    DEAL_PROPOSAL_FETCH_SUCCESS,
    DEAL_PROPOSAL_FETCH_FAILURE
} from '@makemydeal/activities-request-proposal';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import {
    TRADE_IN_SAVE_OR_UPDATE,
    TRADE_IN_UPDATE_REQUEST,
    TRADE_IN_UPDATE_SUCCESS,
    TRADE_IN_UPDATE_FAILURE,
    TRADE_IN_REMOVE_REQUEST,
    TRADE_IN_REMOVE_SUCCESS,
    TRADE_IN_REMOVE_FAILURE
} from '@makemydeal/dr-dash-store/actionTypes/tradeActionTypes';

/**
 * App-level loader configuration for the full-page loader.
 *
 * Composed here (rather than in the store lib) so activity action types can be
 * registered without the store lib depending on individual activities.
 *
 * To add a loader for a new action, add an entry keyed by the trigger action
 * type with a `message` and the `hideOn` action types that dismiss it. Do NOT
 * register actions whose flows already toggle the global spinner manually, or
 * the ref-counted loader will be double-counted.
 */
export const salesCheckinLoaderConfig: LoaderActionConfig = {
    ...loaderActionConfig,
    [FETCH_VEHICLE_DETAILS_REQUEST]: {
        message: 'Loading vehicle details…',
        hideOn: [FETCH_VEHICLE_DETAILS_SUCCESS, FETCH_VEHICLE_DETAILS_FAILURE]
    },
    [Actions.UPDATE_DEAL]: {
        message: 'Updating deal',
        hideOn: [Actions.UPDATE_DEAL_SUCCESS, Actions.UPDATE_DEAL_FAILURE]
    },
    [Actions.DEALER_DETAILS]: {
        message: 'Loading dealership details…',
        hideOn: [Actions.GET_DETAILS_SUCCESS, Actions.GET_DEALER_DETAILS_FAILURE]
    },
    [Actions.FETCH_DEAL_REQUEST]: {
        message: 'Refreshing deal details…',
        hideOn: [Actions.FETCH_DEAL_SUCCESS, Actions.FETCH_DEAL_FAILURE]
    },
    [FETCH_DEAL_SUMMARY_PDF_REQUEST]: {
        message: 'Loading deal summary PDF…',
        hideOn: [DEAL_PDF_LOADED_SUCCESS, FETCH_DEAL_SUMMARY_PDF_FAILURE]
    },
    [DEAL_PROPOSAL_FETCH_REQUEST]: {
        message: 'Loading deal summary…',
        hideOn: [DEAL_PROPOSAL_FETCH_SUCCESS, DEAL_PROPOSAL_FETCH_FAILURE]
    },
    [Actions.FETCH_PDF_SHARE_LINK_REQUEST]: {
        message: 'Preparing your deal PDF link…',
        hideOn: [Actions.FETCH_PDF_SHARE_LINK_SUCCESS, Actions.FETCH_PDF_SHARE_LINK_FAILURE]
    },
    [ACCEPT_DEAL_SUMMARY_REQUEST]: {
        message: 'Accepting deal…',
        hideOn: [Actions.UPDATE_DEAL_SUCCESS, Actions.UPDATE_DEAL_FAILURE]
    },
    [SEND_DEAL_SUMMARY_REQUEST_LOADER]: {
        message: 'Sending request…',
        hideOn: [Actions.UPDATE_DEAL_SUCCESS, Actions.UPDATE_DEAL_FAILURE]
    },
    [TRADE_IN_SAVE_OR_UPDATE]: {
        message: 'Saving trade-in…',
        hideOn: [TRADE_IN_UPDATE_SUCCESS, TRADE_IN_UPDATE_FAILURE],
        when: (state) => !featureToggleSelectors.isTradeInAutoPayoffEnabledSCI(state)
    },
    [TRADE_IN_UPDATE_REQUEST]: {
        message: 'Saving trade-in…',
        hideOn: [TRADE_IN_UPDATE_SUCCESS, TRADE_IN_UPDATE_FAILURE],
        when: (state) => featureToggleSelectors.isTradeInAutoPayoffEnabledSCI(state)
    },
    [TRADE_IN_REMOVE_REQUEST]: {
        message: 'Removing trade-in…',
        hideOn: [TRADE_IN_REMOVE_SUCCESS, TRADE_IN_REMOVE_FAILURE]
    }
};
